import { Component, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import {
  IonButton,
  IonButtons,
  IonChip,
  IonContent,
  IonHeader,
  IonIcon,
  IonSearchbar,
  IonSkeletonText,
  IonTitle,
  IonToolbar,
} from '@ionic/angular/standalone';
import { PosStoreService } from '@adminpages/pos-state';
import { RepairOrder, RepairStatus } from '@adminpages/repairs-api';
import { RepairsStoreService } from '@adminpages/repairs-state';

@Component({
  selector: 'foco-repairs-page',
  standalone: true,
  imports: [FormsModule, RouterLink, IonButton, IonButtons, IonChip, IonContent, IonHeader, IonIcon, IonSearchbar, IonSkeletonText, IonTitle, IonToolbar],
  template: `
    <ion-header>
      <ion-toolbar>
        <ion-title>
          Taller
          <small>{{ pos.selectedWorkspace().name }} · {{ pos.selectedCashRegister()?.name || 'Caja' }}</small>
        </ion-title>
        <ion-buttons slot="end">
          @if (pos.selectedWorkspace().capabilities.pos) {
            <ion-button routerLink="/pos"><ion-icon name="storefront-outline" slot="start"></ion-icon>POS</ion-button>
          }
          @if (pos.selectedWorkspace().capabilities.cash) {
            <ion-button routerLink="/cash-session" [queryParams]="{ scope: 'general', return: '/repairs' }">
              <ion-icon name="cash-outline" slot="start"></ion-icon>Caja
            </ion-button>
          }
          <ion-button routerLink="/settings"><ion-icon name="cog-outline" slot="icon-only"></ion-icon></ion-button>
        </ion-buttons>
      </ion-toolbar>
    </ion-header>

    <ion-content class="pos-page repairs-content">
      <main class="repairs-shell">
        <section class="repairs-toolbar">
          <ion-searchbar
            placeholder="Folio, cliente, equipo o serie"
            [debounce]="350"
            [ngModel]="search()"
            (ngModelChange)="search.set($event); reload()"
          ></ion-searchbar>
          <ion-button routerLink="/repairs/new" size="large">
            <ion-icon name="add" slot="start"></ion-icon>Nueva recepción
          </ion-button>
        </section>

        <section class="status-strip">
          @for (item of filters; track item.value) {
            <ion-chip [class.selected]="status() === item.value" (click)="selectStatus(item.value)">{{ item.label }}</ion-chip>
          }
        </section>

        @if (repairs.errorMessage(); as error) {
          <section class="message error">{{ error }}</section>
        }

        <section class="repair-list">
          @if (repairs.loading()) {
            @for (item of [1, 2, 3, 4]; track item) {
              <article class="repair-card skeleton">
                <ion-skeleton-text animated style="width: 34%; height: 22px"></ion-skeleton-text>
                <ion-skeleton-text animated style="width: 72%"></ion-skeleton-text>
                <ion-skeleton-text animated style="width: 52%"></ion-skeleton-text>
              </article>
            }
          } @else {
            @for (repair of repairs.repairs(); track repair.uuid) {
              <a class="repair-card" [routerLink]="['/repairs', repair.uuid]">
                <div class="repair-main">
                  <span>{{ dateLabel(repair.receivedAt) }}</span>
                  <strong>{{ repair.folio }}</strong>
                  <small>{{ repair.client.name }}{{ repair.client.phone ? ' · ' + repair.client.phone : '' }}</small>
                </div>
                <div class="repair-device">
                  <strong>{{ repair.assetType }}</strong>
                  <small>{{ deviceLabel(repair) }}</small>
                  <small>{{ repair.reportedIssue }}</small>
                </div>
                <div class="repair-state">
                  <span class="status-pill" [class.success]="repair.status === 'ready' || repair.status === 'delivered'" [class.warning]="repair.status === 'quoted' || repair.status === 'waiting_parts'">
                    {{ repair.statusLabel }}
                  </span>
                  @if (repair.balanceDue > 0) { <small>Saldo {{ pos.money(repair.balanceDue) }}</small> }
                </div>
              </a>
            } @empty {
              <section class="message">No hay reparaciones con estos filtros.</section>
            }
          }
        </section>
      </main>
    </ion-content>
  `,
  styles: `
    ion-title small { color: var(--pos-color-muted); display: block; font-size: 11px; font-weight: 500; }
    .repairs-content { --padding-bottom: calc(48px + var(--ion-safe-area-bottom, 0px)); --padding-end: 18px; --padding-start: 18px; --padding-top: 18px; }
    .repairs-shell { display: grid; gap: 14px; margin: 0 auto; max-width: 1180px; }
    .repairs-toolbar { align-items: center; display: grid; gap: 12px; grid-template-columns: minmax(0, 1fr) auto; }
    ion-searchbar { --background: var(--pos-color-surface); --box-shadow: none; --border-radius: 8px; padding: 0; }
    .status-strip { display: flex; gap: 8px; overflow-x: auto; }
    ion-chip { --background: var(--pos-color-surface); border: 1px solid var(--pos-color-border); flex: 0 0 auto; }
    ion-chip.selected { --background: var(--pos-color-primary); color: white; }
    .repair-list { display: grid; gap: 10px; }
    .repair-card { align-items: center; background: var(--pos-color-surface); border: 1px solid var(--pos-color-border); border-radius: 8px; color: var(--pos-color-text); display: grid; gap: 18px; grid-template-columns: minmax(180px, .8fr) minmax(260px, 1.4fr) minmax(150px, .5fr); min-height: 104px; padding: 16px; text-decoration: none; }
    .repair-card:hover { border-color: var(--pos-color-primary); }
    .repair-main, .repair-device, .repair-state { display: grid; gap: 5px; min-width: 0; }
    .repair-main > span, small { color: var(--pos-color-muted); }
    .repair-main strong { font-size: 19px; }
    .repair-device small { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
    .repair-state { justify-items: end; }
    .message { background: var(--pos-color-surface); border: 1px solid var(--pos-color-border); border-radius: 8px; color: var(--pos-color-muted); padding: 18px; }
    .message.error { border-color: var(--pos-color-danger); color: var(--pos-color-danger); }
    @media (max-width: 720px) {
      .repairs-toolbar { grid-template-columns: 1fr; }
      .repair-card { align-items: start; grid-template-columns: 1fr; }
      .repair-state { justify-items: start; }
    }
  `,
})
export class RepairsPageComponent {
  readonly pos = inject(PosStoreService);
  readonly repairs = inject(RepairsStoreService);
  readonly search = signal('');
  readonly status = signal<RepairStatus | undefined>(undefined);
  readonly filters: Array<{ value: RepairStatus | undefined; label: string }> = [
    { value: undefined, label: 'Activas' },
    { value: 'received', label: 'Recibidas' },
    { value: 'diagnosing', label: 'Diagnóstico' },
    { value: 'authorized', label: 'Autorizadas' },
    { value: 'in_repair', label: 'En reparación' },
    { value: 'waiting_parts', label: 'Refacciones' },
    { value: 'ready', label: 'Listas' },
  ];

  constructor() { void this.reload(); }

  selectStatus(status: RepairStatus | undefined): void {
    this.status.set(status);
    void this.reload();
  }

  reload(): Promise<void> { return this.repairs.load({ q: this.search(), status: this.status() }); }
  deviceLabel(repair: RepairOrder): string { return [repair.brand, repair.model, repair.serialNumber].filter(Boolean).join(' · ') || 'Sin datos adicionales'; }
  dateLabel(value?: string): string { return value ? new Intl.DateTimeFormat('es-MX', { dateStyle: 'medium' }).format(new Date(value)) : 'Sin fecha'; }
}
